/*
 * Copyright 2022 Oriflame
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import useAsync from 'react-use/lib/useAsync';
import {
  DEFAULT_NAMESPACE,
  Entity,
  parseEntityRef,
} from '@backstage/catalog-model';
import { useApiHolder } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

/**
 * The one canonical form of a Domain value, used everywhere Domains are
 * discovered, resolved or compared. `spec.domain` is hand-written YAML, so a
 * stray space is a matter of time — and three functions each canonicalising
 * slightly differently is how a Domain ends up listed in the control but
 * matching no rows.
 */
export const domainKey = (domain: string): string =>
  domain.trim().toLocaleLowerCase('en-US');

/**
 * Domain display titles (catalog `kind: Domain`, `metadata.title`) keyed by
 * {@link domainKey} of the Domain's name — loaded by {@link useDomainTree}.
 */
export type DomainTitles = ReadonlyMap<string, string>;

/**
 * A row's Domain as shown to people: the Domain entity's title when the
 * catalog has one, else the raw `spec.domain`; `undefined` without a domain.
 */
export function domainTitleOf(
  domain: string | undefined,
  titles: DomainTitles | undefined,
): string | undefined {
  const raw = domain?.trim();
  if (!raw) return undefined;
  return titles?.get(domainKey(raw)) ?? raw;
}

/**
 * The catalog's Domain hierarchy, as much of it as the board needs: display
 * titles and the `spec.subdomainOf` edges between Domains (feature 0058).
 *
 * Both maps are keyed by {@link domainKey} of the Domain's `metadata.name`, so
 * a hand-written `spec.domain` on a System compares against them without any
 * further canonicalisation.
 */
export interface DomainTree {
  /**
   * `metadata.title` per Domain — the same map `useDomainTitles` returned
   * before this feature, and still the only thing most callers want. A Domain
   * without a title is simply absent, so `domainTitleOf` falls back to the raw
   * `spec.domain` exactly as it did.
   */
  titles: DomainTitles;
  /**
   * Subdomain → parent Domain (`spec.subdomainOf`). A Domain absent from this
   * map is top-level, and the map is **acyclic by construction** — see
   * {@link buildDomainTree}, which is where that invariant is established and
   * on which every consumer depends.
   *
   * An edge is recorded ONLY when the named parent is itself a Domain in the
   * catalog: a `subdomainOf` pointing at something that does not exist would
   * otherwise hide every System under it from the board, and a dangling edge
   * is a catalog mistake, not a reason to lose rows.
   */
  parents: ReadonlyMap<string, string>;
  /**
   * Each Domain's `metadata.namespace`, so a subdomain row can link to the
   * Domain's own page (`/catalog/<ns>/domain/<name>/score`) without assuming
   * `default` — the one place on the board where a Domain's namespace is read
   * rather than merely compared.
   */
  namespaces: ReadonlyMap<string, string>;
}

/** The tree every failure path lands on: titles only, no hierarchy. */
export const EMPTY_DOMAIN_TREE: DomainTree = {
  titles: new Map(),
  parents: new Map(),
  namespaces: new Map(),
};

/**
 * `spec.subdomainOf` as a Domain NAME.
 *
 * Backstage documents the field as an entity *reference*, so
 * `domain:default/retail-platform` and `default/retail-platform` are as valid
 * as the bare `retail-platform` our own catalog happens to write. Comparing
 * the raw string would file a qualified reference as dangling and silently
 * drop the subdomain to the top level, with nothing in the UI to say so.
 *
 * A reference to anything that is not a Domain is ignored rather than
 * believed: `parents` is a Domain-to-Domain map, and the maps it is compared
 * against are keyed by name alone.
 */
function subdomainParentName(value: string): string | undefined {
  try {
    const ref = parseEntityRef(value, {
      defaultKind: 'domain',
      defaultNamespace: DEFAULT_NAMESPACE,
    });
    return ref.kind.toLocaleLowerCase('en-US') === 'domain'
      ? domainKey(ref.name)
      : undefined;
  } catch {
    return undefined;
  }
}

/** Whether `target` is already an ancestor of `from` in the edges built so far. */
function reaches(
  from: string,
  target: string,
  parents: ReadonlyMap<string, string>,
): boolean {
  let current: string | undefined = from;
  while (current !== undefined) {
    if (current === target) return true;
    current = parents.get(current);
  }
  return false;
}

/**
 * Pure half of {@link useDomainTree}: catalog Domain entities → the tree.
 * Separated from the hook so the hierarchy rules — references, dangling
 * parents, cycles — are unit-testable without a catalog API.
 */
export function buildDomainTree(entities: Entity[]): DomainTree {
  const titles = new Map<string, string>();
  const namespaces = new Map<string, string>();
  const known = new Set<string>();
  const claimed = new Map<string, string>();

  for (const entity of entities) {
    const name = entity.metadata.name;
    if (!name) continue;
    const key = domainKey(name);
    known.add(key);

    const title = entity.metadata.title?.trim();
    if (title) {
      titles.set(key, title);
    }

    const namespace = entity.metadata.namespace?.trim();
    if (namespace) {
      namespaces.set(key, namespace);
    }

    const parent = (entity.spec as { subdomainOf?: unknown } | undefined)
      ?.subdomainOf;
    if (typeof parent === 'string' && parent.trim() !== '') {
      const parentName = subdomainParentName(parent);
      if (parentName) claimed.set(key, parentName);
    }
  }

  const parents = new Map<string, string>();
  for (const [child, parent] of claimed) {
    // Self-parenthood and dangling edges both mean "no usable parent".
    if (parent === child || !known.has(parent)) continue;
    // An edge that would close a cycle is dropped, so the map stays a forest.
    // Leaving the cycle in and merely surviving it per-caller is not enough:
    // `topLevelOf` would read every member as top-level while
    // `rootSubdomainOf` reads every member as a subdomain, and the board would
    // then render each System twice, under two group rows sharing one `id`.
    // Breaking it here is the one place that makes every consumer agree.
    if (reaches(parent, child, parents)) continue;
    parents.set(child, parent);
  }

  return { titles, parents, namespaces };
}

/**
 * The Domain this one is a subdomain of, or `undefined` when it is top-level
 * (or unknown to the catalog). Returns a {@link domainKey}, never a raw value.
 */
export function parentOf(
  domain: string | undefined,
  tree: DomainTree | undefined,
): string | undefined {
  const raw = domain?.trim();
  if (!raw || !tree) return undefined;
  return tree.parents.get(domainKey(raw));
}

/**
 * Walks `subdomainOf` up to the top-level ancestor — the Domain this one
 * answers to on the board. Returns a {@link domainKey}; `undefined` only for
 * an empty input.
 *
 * The `seen` guard is belt-and-braces: {@link buildDomainTree} already breaks
 * cycles, so it can only fire on a hand-built tree.
 */
export function topLevelOf(
  domain: string | undefined,
  tree: DomainTree | undefined,
): string | undefined {
  const raw = domain?.trim();
  if (!raw) return undefined;
  const start = domainKey(raw);
  if (!tree) return start;

  const seen = new Set<string>([start]);
  let current = start;
  for (;;) {
    const parent = tree.parents.get(current);
    if (!parent) return current;
    if (seen.has(parent)) return start;
    seen.add(parent);
    current = parent;
  }
}

/**
 * The catalog's Domain tree, fetched ONCE per board in a single request, never
 * per row. {@link EMPTY_DOMAIN_TREE} while loading, on failure, or when no
 * catalog API is registered: callers fall back to the raw `spec.domain` and a
 * flat board, so nothing ever waits on this and a failed lookup costs only the
 * prettier label and the grouping.
 */
export function useDomainTree(): DomainTree {
  // Optional, not `useApi`: a missing catalog API must degrade to raw values,
  // not throw the whole board.
  const catalogApi = useApiHolder().get(catalogApiRef);
  const { value } = useAsync(async (): Promise<DomainTree> => {
    if (!catalogApi) return EMPTY_DOMAIN_TREE;
    const response = await catalogApi.getEntities({
      filter: { kind: 'Domain' },
      fields: [
        'metadata.name',
        'metadata.namespace',
        'metadata.title',
        'spec.subdomainOf',
      ],
    });
    return buildDomainTree(response.items);
  }, [catalogApi]);
  return value ?? EMPTY_DOMAIN_TREE;
}
